import { BaseModel, type RelationConfig } from './base';

import type { TerminologyTermValue } from '../types/interfaces/terminology';

export class Info extends BaseModel {
  id!: number;
  location?: any; // Will be typed properly when Location model is migrated
  employee?: any; // Will be typed properly when Employee model is migrated
  plan?: Plan;
  panels?: Panel[];
  terminology?: Record<string, TerminologyTermValue>;

  static override relations(): RelationConfig {
    return {
      plan: { type: Plan },
      panels: { type: Panel },
    };
  }

  override objectID(): string {
    return this.id?.toString() || '';
  }

  get user(): { firstName: string; lastName: string; image: string | null } | null {
    const item = this.employee ? this.employee : this.location;
    if (item) {
      return {
        firstName: item.firstName,
        lastName: item.lastName,
        image: item.images?.square || null,
      };
    }
    return null;
  }

  get minuteSize(): number {
    const scheduleDisplaySize = this.location?.scheduleDisplaySize || 15;
    return 2 / (scheduleDisplaySize / 15);
  }

  get slotSize(): number {
    const scheduleDisplaySize = this.location?.scheduleDisplaySize || 15;
    return scheduleDisplaySize;
  }
}

export class Plan extends BaseModel {
  id!: number;
  state!: PlanSubscriptionState;

  override objectID(): string {
    return this.id?.toString() || '';
  }

  get isPlanSubscriptionStateTrial(): boolean {
    return this.state === PlanSubscriptionState.trial;
  }

  get isPlanSubscriptionStateActive(): boolean {
    return this.state === PlanSubscriptionState.active;
  }

  get isPlanSubscriptionStateWarning(): boolean {
    return this.state === PlanSubscriptionState.warning;
  }

  get isPlanSubscriptionStatePaymentError(): boolean {
    return this.state === PlanSubscriptionState.paymentError;
  }

  get isPlanSubscriptionStateCancelled(): boolean {
    return this.state === PlanSubscriptionState.cancelled;
  }

  get isPlanSubscriptionStateSuspended(): boolean {
    return this.state === PlanSubscriptionState.suspended;
  }

  get isPlanSubscriptionStateInactive(): boolean {
    return this.state === PlanSubscriptionState.inactive;
  }

  get isPlanSubscriptionStateFree(): boolean {
    return this.state === PlanSubscriptionState.free;
  }

  get isPlanSubscriptionStateTemporary(): boolean {
    return this.state === PlanSubscriptionState.temporary;
  }
}

export class Panel extends BaseModel {
  id!: number;
  action?: PanelAction;

  static override relations(): RelationConfig {
    return {
      action: { type: PanelAction },
    };
  }

  override objectID(): string {
    return this.id?.toString() || '';
  }
}

export class PanelAction extends BaseModel {
  id!: number;

  override objectID(): string {
    return this.id?.toString() || '';
  }
}

export enum PlanSubscriptionState {
  trial = 0,
  active = 1,
  warning = 2,
  paymentError = 3,
  cancelled = 4,
  inactive = 5,
  free = 6,
  suspended = 7,
  temporary = 8,
}

export const PlanSubscriptionStateLabels: Record<PlanSubscriptionState, string> = {
  [PlanSubscriptionState.trial]: 'general.trial',
  [PlanSubscriptionState.active]: 'general.active',
  [PlanSubscriptionState.warning]: 'general.warning',
  [PlanSubscriptionState.paymentError]: 'general.paymentError',
  [PlanSubscriptionState.cancelled]: 'general.cancelled',
  [PlanSubscriptionState.inactive]: 'general.inactive',
  [PlanSubscriptionState.free]: 'auth.free',
  [PlanSubscriptionState.suspended]: 'general.suspended',
  [PlanSubscriptionState.temporary]: 'general.temporary',
}; 